<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor", { default: "About" }),
  title: $.text("Heading", { default: "About Team Shalva" }),
  specialHeading: $.checkbox("Use special heading font", { default: true }),
  mediaType: $.select(
    "Media type",
    [
      { label: "Video", value: "video" },
      { label: "Image", value: "image" },
      { label: "Text only", value: "none" },
    ],
    { default: "video" },
  ),
  videoUrl: $.url("YouTube video URL", {
    default: "https://www.youtube.com/watch?v=Om9eCM1688w",
  }).if('mediaType eq "video"'),
  image: $.upload("Image").if('mediaType eq "image"'),
  imageAlt: $.text("Image description").if('mediaType eq "image"'),
  posterImage: $.upload("Custom video poster").if('mediaType eq "video"'),
  playLabel: $.text("Play button description", {
    default: "Watch About Team Shalva",
  }).if('mediaType eq "video"'),
  richContent: $.richtext("Content", { simple: true }),
  layout: $.select(
    "Layout",
    [
      { label: "Media then text", value: "media-start" },
      { label: "Text then media", value: "media-end" },
      { label: "Stacked", value: "stacked" },
    ],
    { default: "media-start" },
  ).if('mediaType ne "none"'),
  width: $.select(
    "Content width",
    [
      { label: "Regular", value: "regular" },
      { label: "Narrow", value: "narrow" },
    ],
    { default: "regular" },
  ),
  surface: $.select(
    "Background",
    [
      { label: "Light brand", value: "light" },
      { label: "White surface", value: "surface" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "light" },
  ),
  backgroundImage: $.upload("Background image").if('surface eq "image"'),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if('surface eq "image" and backgroundImage pr'),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    'surface eq "image" and backgroundImage pr',
  ),
  buttonLabel: $.text("Button label"),
  buttonTo: $.text("Button destination").if("buttonLabel pr"),
});
export const EventAbout = defineBlock({
  name: "EventAbout",
  label: "About",
  icon: "play_circle",
  thumbnail: "/block-thumbs/EventAbout.svg",
  category: "Event",
  order: 50,
  schema,
})
</script>
<script setup lang="ts">
import { computed } from "vue";

import { Btn, Icon, pathKeyToURL, sanitizeHtml } from "@bagelink/vue"


;

const aboutProps = defineProps<{
  anchor?: string;
  title?: string;
  specialHeading?: boolean;
  mediaType?: "video" | "image" | "none";
  videoUrl?: string;
  image?: string;
  imageAlt?: string;
  posterImage?: string;
  playLabel?: string;
  richContent?: string;
  layout?: "media-start" | "media-end" | "stacked";
  width?: "regular" | "narrow";
  surface?: "light" | "surface" | "brand" | "dark" | "image";
  backgroundImage?: string;
  backgroundFit?: "cover" | "contain" | "fill";
  dimBackground?: boolean;
  buttonLabel?: string;
  buttonTo?: string;
}>();

const aboutVideoId = computed(() => {
  try {
    const url = new URL(aboutProps.videoUrl ?? "");
    if (!["https:", "http:"].includes(url.protocol)) return "";
    const host = url.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") {
      id = url.pathname.slice(1);
    } else if (
      ["youtube.com", "m.youtube.com", "youtube-nocookie.com"].includes(host)
    ) {
      id =
        url.pathname === "/watch"
          ? (url.searchParams.get("v") ?? "")
          : (url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)\/?$/)?.[1] ??
            "");
    }
    return /^[\w-]{11}$/.test(id) ? id : "";
  } catch {
    return "";
  }
});
const aboutPlaybackUrl = computed(() =>
  aboutVideoId.value
    ? `https://www.youtube.com/watch?v=${aboutVideoId.value}`
    : "",
);
const aboutPoster = computed(() =>
  aboutProps.posterImage
    ? pathKeyToURL(aboutProps.posterImage) || aboutProps.posterImage
    : aboutVideoId.value
      ? `https://i.ytimg.com/vi/${aboutVideoId.value}/maxresdefault.jpg`
      : "",
);
const aboutSurfaceClass = computed(
  () =>
    ({
      light: "bg-primary-light",
      surface: "bg-box",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "",
    })[aboutProps.surface ?? "light"],
);
const aboutGridClass = computed(() =>
  aboutProps.mediaType === "none"
    ? "grid txt-center"
    : aboutProps.layout === "stacked"
      ? "grid gap-2 m_gap-1"
      : "grid grid-wrap-2 gap-2 m_gap-1 align-items-start",
);
const aboutShellStyle = computed(() =>
  aboutProps.surface === "image" && aboutProps.backgroundImage
    ? {
        backgroundImage: `${aboutProps.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(aboutProps.backgroundImage) || aboutProps.backgroundImage}")`,
        backgroundSize:
          aboutProps.backgroundFit === "fill"
            ? "100% 100%"
            : (aboutProps.backgroundFit ?? "cover"),
      }
    : undefined,
);
</script>

<template>
  <section
    :id="anchor"
    class="blox-background-image relative px-2 py-5 m_px-1 m_py-2"
    :class="aboutSurfaceClass"
    :style="aboutShellStyle"
  >
    <h2
      v-if="title"
      class="txt-70 m_txt-40 txt-center m-0 line-height-1 m_line-height-11 balance pb-1"
      :class="{ 'blox-special-heading': specialHeading }"
    >
      {{ title }}
    </h2>
    <div
      :class="[
        aboutGridClass,
        mediaType === 'none' || width === 'narrow' ? 'w-800px' : 'w-1200px',
      ]"
    >
      <div v-if="mediaType !== 'none' && layout !== 'media-end'">
        <div
          v-if="mediaType === 'video' && aboutPlaybackUrl"
          :key="aboutPlaybackUrl"
          v-lightbox="{
            src: aboutPlaybackUrl,
            type: 'video',
            name: playLabel || title,
          }"
          class="hover relative overflow-hidden radius-1"
          :aria-label="playLabel"
        >
          <img
            :src="aboutPoster"
            alt=""
            class="w-100p ratio-16-9 cover block"
            loading="lazy"
          />
          <Icon
            name="play_circle"
            :size="4"
            class="absolute center-xy color-white keep-white shadow-txt"
          />
        </div>
        <img
          v-else-if="mediaType === 'image' && image"
          :src="pathKeyToURL(image) || image"
          :alt="imageAlt"
          class="w-100p ratio-4-3 cover radius-1"
          loading="lazy"
        />
        <div
          v-else-if="mediaType !== 'none'"
          class="w-100p ratio-4-3 bg-box border radius-1 flex-center opacity-6"
        >
          <Icon
            :name="mediaType === 'video' ? 'play_circle' : 'image'"
            :size="4"
            class="color-gray"
          />
        </div>
      </div>
      <div v-if="richContent || (buttonLabel && buttonTo)">
        <!-- eslint-disable vue/no-v-html -- Rich text is sanitized before rendering. -->
        <div
          v-if="richContent"
          class="pretty rich-text"
          v-html="sanitizeHtml(richContent)"
        />
        <!-- eslint-enable vue/no-v-html -->
        <Btn
          v-if="buttonLabel && buttonTo"
          class="mt-1"
          :value="buttonLabel"
          :to="buttonTo"
        />
      </div>
      <div v-if="mediaType !== 'none' && layout === 'media-end'">
        <div
          v-if="mediaType === 'video' && aboutPlaybackUrl"
          :key="aboutPlaybackUrl"
          v-lightbox="{
            src: aboutPlaybackUrl,
            type: 'video',
            name: playLabel || title,
          }"
          class="hover relative overflow-hidden radius-1"
          :aria-label="playLabel"
        >
          <img
            :src="aboutPoster"
            alt=""
            class="w-100p ratio-16-9 cover block"
            loading="lazy"
          />
          <Icon
            name="play_circle"
            :size="4"
            class="absolute center-xy color-white keep-white shadow-txt"
          />
        </div>
        <img
          v-else-if="mediaType === 'image' && image"
          :src="pathKeyToURL(image) || image"
          :alt="imageAlt"
          class="w-100p ratio-4-3 cover radius-1"
          loading="lazy"
        />
        <div
          v-else-if="mediaType !== 'none'"
          class="w-100p ratio-4-3 bg-box border radius-1 flex-center opacity-6"
        >
          <Icon
            :name="mediaType === 'video' ? 'play_circle' : 'image'"
            :size="4"
            class="color-gray"
          />
        </div>
      </div>
    </div>
  </section>
</template>
